'use client';

import { useEffect, useRef, useState } from 'react';

interface UseMediaInViewportOptions {
  disabled?: boolean;
  rootMargin?: string;
}

/**
 * Keeps heavy media URLs detached until their element actually reaches the
 * viewport. Older browsers fall back to a bounded scroll/resize check.
 */
export function useMediaInViewport<T extends Element>({
  disabled = false,
  rootMargin = '0px',
}: UseMediaInViewportOptions = {}) {
  const targetRef = useRef<T | null>(null);
  const [hasEnteredViewport, setHasEnteredViewport] = useState(disabled);

  useEffect(() => {
    if (disabled) {
      setHasEnteredViewport(true);
      return;
    }

    if (hasEnteredViewport) return;
    const element = targetRef.current;
    if (!element) return;

    const IntersectionObserverCtor = window.IntersectionObserver;
    if (typeof IntersectionObserverCtor !== 'function') {
      const checkViewport = () => {
        const rect = element.getBoundingClientRect();
        const viewportHeight = Math.min(window.innerHeight, document.documentElement.clientHeight);
        const viewportWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
        const isVisible =
          rect.bottom > 0 &&
          rect.right > 0 &&
          rect.top < viewportHeight &&
          rect.left < viewportWidth;

        if (!isVisible) return;
        setHasEnteredViewport(true);
        window.removeEventListener('scroll', checkViewport);
        window.removeEventListener('resize', checkViewport);
      };

      // Let streamed content and styles establish the final layout before the
      // first measurement, otherwise a below-fold element can briefly report
      // a top-of-page rect during hydration.
      const initialCheck = window.setTimeout(checkViewport, 100);
      window.addEventListener('scroll', checkViewport, { passive: true });
      window.addEventListener('resize', checkViewport);
      return () => {
        window.clearTimeout(initialCheck);
        window.removeEventListener('scroll', checkViewport);
        window.removeEventListener('resize', checkViewport);
      };
    }

    const observer = new IntersectionObserverCtor(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setHasEnteredViewport(true);
        observer.disconnect();
      },
      { rootMargin, threshold: 0.01 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [disabled, hasEnteredViewport, rootMargin]);

  return {
    targetRef,
    shouldLoadMedia: disabled || hasEnteredViewport,
  };
}
